import React, { useContext, useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  Alert,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';
import { format } from 'date-fns';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import CustomDialog from '../custom-dialog/custom-dialog';
import { PrintWaybillsMultiSizes } from '../bulk-action/print-waybills';

interface StampData {
  pickup_request_stamp: string | null;
  order_count: number;
  packages_count: number;
}

interface TimestampsResponse {
  pickup_task_id: number;
  total_orders: number;
  total_packages: number;
  stamps: StampData[];
}

interface PickupTaskWaybillsDialogProps {
  pickupTaskId: string;
  onClose: () => void;
}

/**
 * Waybill printing dialog with simplified workflow:
 * Step 1: Select timestamp (or all orders)
 * Step 2: Fetch full task details with loading indicator
 * Step 3: Show PrintWaybillsMultiSizes with filtered orders
 *         (User selects format A4/mini sticker within that component)
 */
function PickupTaskWaybillsDialog({
  pickupTaskId,
  onClose,
}: PickupTaskWaybillsDialogProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [timestampsData, setTimestampsData] =
    useState<TimestampsResponse | null>(null);

  const [confirmedSelectedTimestamp, setConfirmedSelectedTimestamp] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState<OrderType[]>([]);

  const { config } = useContext(AuthContext);

  useEffect(() => {
    if (pickupTaskId) {
      fetchTimestamps();
    }
  }, [pickupTaskId]);

  const fetchTimestamps = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${pickupTaskId}/request-stamps/`;
      console.log('Fetching timestamps from:', endpoint);
      const response = await axios.get<TimestampsResponse>(endpoint, config);
      console.log('Timestamps response:', response.data);
      setTimestampsData(response.data);
    } catch (err) {
      console.error('Error fetching timestamps:', err);
      setError('Failed to fetch pickup task details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const handlePrintClick = async (timestamp: string) => {
    if (!timestampsData) return;

    // Fetch full task details to get complete order data
    setIsLoading(true);
    setError(null);
    
    try {
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${pickupTaskId}/`;
      console.log('Fetching full task details:', endpoint);
      const response = await axios.get<IPickupTask>(endpoint, config);
      const fullTask = response.data;
      
      // Map timestamp selection to orders
      let ordersToShow: OrderType[] = [];

      if (timestamp === 'all') {
        ordersToShow = fullTask.orders;
        console.log('PickupTaskWaybillsDialog - ALL orders:', ordersToShow.length);
      } else {
        // Filter orders by the selected timestamp
        // Handle 'null' string for legacy orders without timestamp
        const searchTimestamp = timestamp === 'null' ? null : timestamp;
        ordersToShow = fullTask.orders.filter((order) => 
          order.pickup_request_stamp === searchTimestamp
        );
        console.log('PickupTaskWaybillsDialog - Specific timestamp orders:', ordersToShow.length);
      }

      setSelectedOrders(ordersToShow);
      setConfirmedSelectedTimestamp(timestamp);
    } catch (err) {
      console.error('Error fetching task details:', err);
      setError('Failed to fetch task details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) {
      return '';
    }
    try {
      return format(new Date(timestamp), 'PPpp'); // e.g., "Apr 29, 2021, 9:30:00 AM"
    } catch (err) {
      return timestamp;
    }
  };



  // Show print interface after timestamp is confirmed and orders are loaded
  if (confirmedSelectedTimestamp) {
    return (
      <CustomDialog open={isOpen} onClose={handleClose}  fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography align={"center"} variant="h6">Print Waybills</Typography>
            <IconButton onClick={handleClose}>
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <PrintWaybillsMultiSizes
            pickupTaskId={pickupTaskId}
            orders={selectedOrders}
            timestamp={confirmedSelectedTimestamp}
            onClose={handleClose}
          />
        </DialogContent>
      </CustomDialog>
    );
  }


  // Step 1: Timestamp selection
  return (
    <CustomDialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Select which orders to print</Typography>
          <IconButton onClick={handleClose}>
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" gutterBottom>
              Please wait...
            </Typography>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : timestampsData ? (
          <List sx={{ width: '100%' }}>
            {/* All Orders Option */}
            <ListItem
              disablePadding
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ListItemButton onClick={() => handlePrintClick('all')}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">
                        <strong>All Orders</strong>
                      </Typography>
                      <Chip
                        label={`${timestampsData.total_orders} orders • ${timestampsData.total_packages} packages`}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  }
                />
                <IconButton edge="end" color="primary">
                  <PrintIcon />
                </IconButton>
              </ListItemButton>
            </ListItem>

            {/* Individual Timestamp Options */}
            {timestampsData.stamps.map((stamp, index) => (
              <ListItem
                key={stamp.pickup_request_stamp || 'null'}
                disablePadding
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <ListItemButton 
                  onClick={() => handlePrintClick(stamp.pickup_request_stamp || 'null')}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          <strong>Request #{(timestampsData.stamps.length - index)}</strong>
                        </Typography>
                        <Chip
                          label={`${stamp.order_count} orders • ${stamp.packages_count} packages`}
                          size="small"
                          color="secondary"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(stamp.pickup_request_stamp)}
                      </Typography>
                    }
                  />
                  <IconButton edge="end" color="primary">
                    <PrintIcon />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        ) : (
          <Alert severity="warning">No data available</Alert>
        )}
      </DialogContent>

    </CustomDialog>
  );
}

export default PickupTaskWaybillsDialog;
